/**
 * Reachability probe behind the no-network page — pure, React-free, injectable.
 *
 * ── Why a real request and not `navigator.onLine` ───────────────────────────
 * `navigator.onLine` answers "does the OS think an adapter is up", which is
 * false-negative on Windows with a VPN or a virtual adapter (Hyper-V, Docker,
 * WSL) even when the internet works, and true when the router is up but the
 * ISP is down. So it is never trusted alone: the only honest answer is a
 * round trip. Ported from the traveller app's `useAppInitialization.ts`,
 * which learned this the hard way.
 *
 * ── Why HEAD on the favicon ────────────────────────────────────────────────
 * `public/favicon.ico` is same-origin (no CORS), always deployed, and 201 KB —
 * HEAD proves the server answered without downloading the body every 1.8 s.
 * The URL is the LITERAL `/favicon.ico`, never `<link rel=icon>`'s href: the
 * partner portal rewrites that link to the partner's CDN favicon
 * (`partner/theme/brandTokens.js`, `applyFavicon`), and a cross-origin HEAD
 * without CORS headers rejects and would read as "offline".
 *
 * ── Why any status counts as reachable ─────────────────────────────────────
 * A 404 (or an SPA rewrite answering with index.html) still means the network
 * round trip completed. Only a rejection or a timeout is "unreachable". The
 * cache-buster plus `cache: 'no-store'` guarantee a real network hit — there
 * is no service worker in this app to answer from.
 *
 * ── Why a timeout ──────────────────────────────────────────────────────────
 * A black-holed route (router up, ISP down) does not reject; it hangs. A probe
 * that cannot complete inside PROBE_TIMEOUT_MS is a failure — "unusably slow"
 * counts as offline. `Promise.race` keeps that true even for a fetch that
 * ignores its abort signal.
 *
 * Everything is injectable (`fetchImpl`, `timeoutMs`, `probe`) so the tests
 * never have to stub globals, and `fetch` is resolved at CALL time so a
 * `global.fetch = jest.fn()` assigned in `beforeEach` is honoured.
 */

/** Same-origin; `PUBLIC_URL` is '' in this build (no "homepage") and under `react-scripts test`. */
export const PROBE_URL = `${process.env.PUBLIC_URL || ''}/favicon.ico`;

/** A probe that cannot finish in this window is a failure. */
export const PROBE_TIMEOUT_MS = 11000;

/**
 * How long connectivity must fail CONTINUOUSLY before the page shows, measured
 * from the START of the first failed probe. Long enough that a one-second blip
 * never flashes the page; short enough that a real outage is named quickly.
 */
export const OFFLINE_THRESHOLD_MS = 11000;

/** Re-probe cadence once a failure streak has started. Nothing polls while online. */
export const FAILING_RECHECK_MS = 1800;

export const probeUrl = (now = Date.now) => `${PROBE_URL}?_=${now()}`;

/**
 * true = the server answered (any status, 404 included); false = rejected,
 * aborted, timed out, or threw. Never rejects.
 */
export async function probeConnectivity({ fetchImpl, timeoutMs = PROBE_TIMEOUT_MS } = {}) {
  // Fail open: with no fetch at all there is nothing to judge with, and a
  // portal blocked behind an offline page it can never clear is the worse bug.
  if (!fetchImpl && (typeof window === 'undefined' || typeof window.fetch !== 'function')) return true;
  const doFetch = fetchImpl || ((...args) => window.fetch(...args));

  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      if (controller) controller.abort();
      reject(new Error('connectivity probe timed out'));
    }, timeoutMs);
  });

  try {
    await Promise.race([
      doFetch(probeUrl(), {
        method: 'HEAD',
        cache: 'no-store',
        mode: 'same-origin',
        signal: controller ? controller.signal : undefined,
      }),
      timeout,
    ]);
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Delay before the next re-probe, given how long the current failure streak
 * has lasted. Two jobs: land the page at EXACTLY OFFLINE_THRESHOLD_MS (the
 * last pre-threshold gap is shortened so a probe fires at 11.0 s rather than
 * at the next 1.8 s multiple, 12.6 s), and never return zero — a tight loop
 * past the threshold would hammer the server for the whole outage.
 */
export function nextRecheckDelay(elapsedMs) {
  if (elapsedMs >= OFFLINE_THRESHOLD_MS) return FAILING_RECHECK_MS;
  return Math.max(1, Math.min(FAILING_RECHECK_MS, OFFLINE_THRESHOLD_MS - elapsedMs));
}

/**
 * Resolves on the first successful probe, re-probing every `intervalMs`.
 *
 * No cancellation on purpose: its only caller is the chunk loader in
 * `lazyWithRetry`, and `React.lazy` caches the factory promise for the life
 * of the module — there is nothing to cancel, and a promise that stays
 * pending until the network returns is exactly what Suspense wants.
 */
export async function waitUntilReachable({ probe = probeConnectivity, intervalMs = FAILING_RECHECK_MS } = {}) {
  while (!(await probe())) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}
